import { useEffect, useRef } from 'react';
import { validateZip, validatePhone } from '@de/utils/validateInputs';
import type { ValidationError } from '@de/providers/dynamicExperienceReducer';

interface FieldValidationConfig {
  field: string;
  minDigits: number;
  validate: (digits: string) => Promise<boolean>;
  errorMessage: string;
}

const FIELD_CONFIGS: Record<string, FieldValidationConfig> = {
  zip: {
    field: 'zip',
    minDigits: 5,
    validate: async (digits) => {
      const result = await validateZip(digits);
      return !!result?.validZip;
    },
    errorMessage: 'Please enter a valid ZIP code',
  },
  phoneNumber: {
    field: 'phoneNumber',
    minDigits: 10,
    validate: async (digits) => {
      const result = await validatePhone(digits);
      return !!result?.validPhoneNumber;
    },
    errorMessage: 'Please enter a valid phone number',
  },
};

export function useFieldValidation(
  leadFieldAssociated: string | undefined,
  inputValue: string,
  setValidationErrors: (errors: ValidationError[]) => void,
  clearValidationErrors: () => void,
) {
  const validatedRef = useRef<string | null>(null);
  const fieldKey = leadFieldAssociated?.toString() || '';
  const config = FIELD_CONFIGS[fieldKey];

  useEffect(() => {
    if (!config) return;

    const digits = inputValue.replace(/\D/g, '');

    if (digits.length === 0) {
      validatedRef.current = null;
      return;
    }

    if (digits.length < config.minDigits) {
      validatedRef.current = null;
      setValidationErrors([{ field: config.field, message: '' }]);
      return;
    }

    if (validatedRef.current === digits) return;

    let cancelled = false;

    const runValidation = async () => {
      const isValid = await config.validate(digits);
      if (cancelled) return;

      validatedRef.current = digits;

      if (!isValid) {
        setValidationErrors([
          { field: config.field, message: config.errorMessage },
        ]);
      } else {
        clearValidationErrors();
      }
    };

    runValidation();

    return () => {
      cancelled = true;
    };
  }, [config, inputValue, setValidationErrors, clearValidationErrors]);
}
